/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';

export function getShowingRange(total: number, page: number, perPage: number) {
    const safeTotal = Math.max(0, total ?? 0);
    const safePer   = Math.max(1, perPage ?? 1);
    const safePage  = Math.max(1, page ?? 1);

    if (!safeTotal) return { start: 0, end: 0, total: 0 };
    const start = (safePage - 1) * safePer + 1;
    const end   = Math.min(safePage * safePer, safeTotal);
    return { start, end, total: safeTotal };
}

/* =========================
   Showing (no-wrap, one line)
   ========================= */
type ShowingProps = {
    total: number;
    page: number;
    perPage: number;
    color?: string;
};
export const ShowingInline: React.FC<ShowingProps> = ({ total, page, perPage, color = '#667085' }) => {
    const { start, end } = getShowingRange(total, page, perPage);

    return (
        <div
            style={{
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: 6,
                whiteSpace: 'nowrap',
                color,
                fontStyle: 'italic',
            }}
        >
            <span>Showing</span>
            <span style={{ fontWeight: 700, color: '#344054', fontStyle: 'normal' }}>{start}-{end}</span>
            <span style={{ fontStyle: 'normal' }}>of</span>
            <span style={{ fontWeight: 700, color: '#344054', fontStyle: 'normal' }}>{total}</span>
        </div>
    );
};

/* =========================
   Pagination (pure inline styles)
   ========================= */
type PaginationProps = {
    total: number;
    page: number;      // 1-based
    perPage: number;
    onChange: (page: number) => void;
};

export const PaginationInline: React.FC<PaginationProps> = ({
                                                                total,
                                                                page,
                                                                perPage,
                                                                onChange,
                                                            }) => {
    const totalPages = Math.max(1, Math.ceil((total ?? 0) / Math.max(1, perPage)));
    const clamp = (n: number, min = 1, max = totalPages) => Math.max(min, Math.min(max, n));
    const cur = clamp(page);

    const windowSize = 5;
    const half = Math.floor(windowSize / 2);
    let start = clamp(cur - half, 1, Math.max(1, totalPages - windowSize + 1));
    let end   = clamp(start + windowSize - 1);
    if (end - start + 1 < windowSize) start = clamp(end - windowSize + 1);

    const nums: number[] = [];
    for (let i = start; i <= end; i++) nums.push(i);

    const isFirst = cur === 1;
    const isLast  = cur === totalPages;
    const go = (p: number) => () => onChange(clamp(p));

    // palette + styles (inline only)
    const palette = {
        border: '#E4E7EC',
        active: '#57C1E9',
        text:   '#57C1E9',
        textDark: '#1F2937',
        disabled: '#98A2B3',
        bg: '#FFFFFF',
        bgHover: '#F3F5F9',
        bgDisabled: '#F8FAFC',
    };

    const ulStyle: React.CSSProperties = {
        display: 'inline-flex',
        paddingLeft: 0,
        margin: 0,
        border: `1px solid ${palette.border}`,
        borderRadius: 0,
        overflow: 'hidden',
        background: palette.bg,
        listStyle: 'none',
    };

    const aBase: React.CSSProperties = {
        position: 'relative',
        display: 'block',
        minWidth: 40,
        padding: '8px 14px',
        textAlign: 'center',
        lineHeight: 1.2,
        color: palette.text,
        textDecoration: 'none',
        backgroundColor: palette.bg,
        borderLeft: `1px solid ${palette.border}`,
        cursor: 'pointer',
        userSelect: 'none',
    };

    const aActive: React.CSSProperties = {
        ...aBase,
        color: '#fff',
        backgroundColor: palette.active,
        borderColor: palette.active,
        cursor: 'default',
    };

    const aDisabled: React.CSSProperties = {
        ...aBase,
        color: palette.disabled,
        backgroundColor: palette.bgDisabled,
        cursor: 'not-allowed',
        pointerEvents: 'none',
    };

    // helper to render each button with inline styles only
    const renderItem = (
        label: React.ReactNode,
        onClick: (() => void) | undefined,
        key: React.Key,
        opts: { active?: boolean; disabled?: boolean; isFirst?: boolean } = {}
    ) => {
        const style = opts.active
            ? aActive
            : opts.disabled
                ? aDisabled
                : { ...aBase };

        // remove left border for the very first item
        if (opts.isFirst) style.borderLeft = 'none';

        return (
            <li key={key} style={{ display: 'inline' }}>
                <button type="button" onClick={onClick} aria-current={opts.active ? 'page' : undefined} style={style}>
                    {label}
                </button>
            </li>
        );
    };

    // build items
    const items: React.ReactNode[] = [];
    let index = 0;

    items.push(renderItem('«', !isFirst ? go(1) : undefined, `first`, { disabled: isFirst, isFirst: index++ === 0 }));
    items.push(renderItem('‹', !isFirst ? go(cur - 1) : undefined, `prev`,  { disabled: isFirst, isFirst: index++ === 0 }));

    nums.forEach((n) => {
        items.push(
            renderItem(n, n !== cur ? go(n) : undefined, `p-${n}`, {
                active: n === cur,
                isFirst: index++ === 0,
            })
        );
    });

    items.push(renderItem('›', !isLast ? go(cur + 1) : undefined, `next`, { disabled: isLast, isFirst: index++ === 0 }));
    items.push(renderItem('»', !isLast ? go(totalPages) : undefined, `last`, { disabled: isLast, isFirst: index++ === 0 }));

    return (
        <nav aria-label="Pagination" style={{ display: 'inline-block' }}>
            <ul style={ulStyle}>{items}</ul>
        </nav>
    );
};
